import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LatestCommentsPage.css';

function LatestCommentsPage() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const commentsPerPage = 20;

  useEffect(() => {
    fetchLatestComments();
  }, []);

  const fetchLatestComments = async () => {
    try {
      setLoading(true);
      const apiBase = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
      const response = await fetch(`${apiBase}/api/comments/latest`);

      if (!response.ok) {
        throw new Error('최신 댓글을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      setComments(data.comments || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching latest comments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    // XE 날짜 형식: 20040630210542 -> YYYY-MM-DD HH:MM
    if (!dateString) return '';
    const str = String(dateString);
    const year = str.substring(0, 4);
    const month = str.substring(4, 6);
    const day = str.substring(6, 8);
    const hour = str.substring(8, 10);
    const minute = str.substring(10, 12);
    return `${year}-${month}-${day} ${hour}:${minute}`;
  };

  const getBoardName = (boardId) => {
    const boardNames = {
      'sermon': '설교모음',
      'mark': '매일묵상',
      'notice': '알림방',
      'free': '사랑채',
      'critique': '설교비평',
      'pannenberg': '판넨베르크 아카이브',
      'member': '후원안내'
    };
    return boardNames[boardId] || boardId;
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const truncateText = (text, maxLength = 100) => {
    const plainText = stripHtml(text);
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  // 페이지네이션
  const indexOfLastComment = currentPage * commentsPerPage;
  const indexOfFirstComment = indexOfLastComment - commentsPerPage;
  const currentComments = comments.slice(indexOfFirstComment, indexOfLastComment);
  const totalPages = Math.ceil(comments.length / commentsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="latest-comments-page">
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="latest-comments-page">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="latest-comments-page">
      <div className="container">
        <h1 className="page-title">최신 댓글</h1>

        <div className="comments-stats">
          <p>전체 <strong>{comments.length}</strong>개의 댓글</p>
        </div>

        <div className="comments-list">
          {currentComments.length === 0 ? (
            <div className="no-comments">등록된 댓글이 없습니다.</div>
          ) : (
            currentComments.map((comment) => (
              <div key={comment.comment_srl} className="comment-item">
                <div className="comment-header">
                  <div className="comment-meta">
                    <Link
                      to={`/board/${comment.module_srl}`}
                      className="board-badge"
                    >
                      {getBoardName(comment.module_srl)}
                    </Link>
                    <span className="comment-author">{comment.nick_name || comment.user_name}</span>
                    <span className="comment-date">{formatDate(comment.regdate)}</span>
                  </div>
                </div>

                <div className="comment-content">
                  <Link
                    to={`/board/${comment.module_srl}/${comment.document_srl}`}
                    className="comment-text"
                  >
                    {truncateText(comment.content, 150)}
                  </Link>
                </div>

                {comment.document_title && (
                  <div className="comment-post-info">
                    <span className="post-label">원글:</span>
                    <Link
                      to={`/board/${comment.module_srl}/${comment.document_srl}`}
                      className="post-title"
                    >
                      {comment.document_title}
                    </Link>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="page-btn"
            >
              이전
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              // 현재 페이지 주변 5페이지만 표시
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
              ) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={currentPage === pageNumber ? 'page-btn active' : 'page-btn'}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 3 ||
                pageNumber === currentPage + 3
              ) {
                return <span key={pageNumber}>...</span>;
              }
              return null;
            })}

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="page-btn"
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LatestCommentsPage;
